import {
  DEFAULT_EXERCISE_PRESCRIPTION_TYPE,
  ExercisePrescriptionLabels,
  ExercisePrescriptionTypes,
} from "data/exercisePrescription";

const padNumber = (value) => (value < 10 ? `0${value}` : String(value));

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const coerceSetsCount = (setsValue) => {
  if (setsValue === undefined || setsValue === null) {
    return null;
  }

  if (Array.isArray(setsValue)) {
    return setsValue.length;
  }

  if (typeof setsValue === "number") {
    return setsValue;
  }

  if (isNonEmptyString(setsValue)) {
    return setsValue;
  }

  return null;
};

const pushDetailRow = (target, label, value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (typeof value === "string" && value.length === 0) {
    return;
  }

  target.push({ label, value });
};

export const formatDuration = (seconds) => {
  if (typeof seconds === "string") {
    const trimmed = seconds.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  const numericValue = Number(seconds);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  const totalSeconds = Math.round(numericValue);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  const parts = [];

  if (hours) {
    parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
  }
  if (minutes) {
    parts.push(`${minutes} min`);
  }
  if (remainingSeconds || parts.length === 0) {
    parts.push(`${remainingSeconds} sec`);
  }

  return parts.join(" ");
};

export const formatTimer = (seconds) => {
  const numericValue = Number(seconds);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  const totalSeconds = Math.round(numericValue);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${padNumber(remainingSeconds)}`;
};

export const formatDistance = (kilometers) => {
  if (typeof kilometers === "string") {
    const trimmed = kilometers.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  const numericValue = Number(kilometers);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  if (numericValue >= 1) {
    const rounded = Math.round(numericValue * 100) / 100;
    return `${rounded
      .toString()
      .replace(/\.0+$/, "")
      .replace(/(\.\d*[1-9])0+$/, "$1")} km`;
  }

  const meters = Math.round(numericValue * 1000);
  return `${meters} m`;
};

export const resolvePrescriptionType = (exercise) =>
  exercise?.prescriptionType || DEFAULT_EXERCISE_PRESCRIPTION_TYPE;

export const getPrescriptionLabel = (type) =>
  ExercisePrescriptionLabels[type] || type;

export const buildPrescriptionSummary = (exercise) => {
  const type = resolvePrescriptionType(exercise);
  const summaryChips = [];
  const detailRows = [];

  const restValue = exercise?.rest;
  const setsValue = coerceSetsCount(exercise?.sets);
  const setsToFailure = exercise?.setsToFailure ?? 0;
  const repsValue = exercise?.repsPattern || exercise?.reps || null;

  switch (type) {
    case ExercisePrescriptionTypes.DURATION: {
        const duration =
          (typeof exercise?.durationValue === "string" &&
            exercise.durationValue.trim().length > 0 &&
            exercise.durationValue.trim()) ||
          formatDuration(exercise?.durationSeconds);
      if (duration) {
        summaryChips.push(`Duration: ${duration}`);
      }
      if (restValue) {
        detailRows.push({ label: "Rest", value: restValue });
      }
      break;
    }
    case ExercisePrescriptionTypes.DISTANCE: {
        const distance =
          (typeof exercise?.distanceValue === "string" &&
            exercise.distanceValue.trim().length > 0 &&
            exercise.distanceValue.trim()) ||
          formatDistance(exercise?.distanceKilometers);
      if (distance) {
        summaryChips.push(`Distance: ${distance}`);
      }
      if (restValue) {
        detailRows.push({ label: "Rest", value: restValue });
      }
      break;
    }
    case ExercisePrescriptionTypes.INTERVAL: {
        const work =
          (typeof exercise?.intervalWorkValue === "string" &&
            exercise.intervalWorkValue.trim().length > 0 &&
            exercise.intervalWorkValue.trim()) ||
          formatDuration(exercise?.intervalWorkSeconds);
        const rest =
          (typeof exercise?.intervalRestValue === "string" &&
            exercise.intervalRestValue.trim().length > 0 &&
            exercise.intervalRestValue.trim()) ||
          formatDuration(exercise?.intervalRestSeconds);
      const rounds = Number(exercise?.intervalRounds) || null;
      if (work) {
        summaryChips.push(`Work: ${work}`);
      }
      if (rest) {
        summaryChips.push(`Rest: ${rest}`);
      }
      if (rounds) {
        summaryChips.push(`Rounds: ${rounds}`);
      }
      break;
    }
    default: {
      if (setsValue) {
        summaryChips.push(`Sets: ${setsValue}`);
      }
      if (repsValue) {
        summaryChips.push(`Reps: ${repsValue}`);
      }
      if (exercise?.tempo) {
        pushDetailRow(detailRows, "Tempo", exercise.tempo);
      }
      if (restValue) {
        pushDetailRow(detailRows, "Rest", restValue);
      }
      if (setsToFailure) {
        pushDetailRow(detailRows, "To Failure", `${setsToFailure} sets`);
      }
    }
  }

  return {
    type,
    summaryChips,
    detailRows,
  };
};

export const buildSupersetSummary = (exercise) => {
  const summary = buildPrescriptionSummary(exercise);
  const label = getPrescriptionLabel(summary.type);
  return {
    ...summary,
    label,
  };
};

export const shouldHideStandardFields = (exercise) => {
  const type = resolvePrescriptionType(exercise);
  return (
    type === ExercisePrescriptionTypes.DURATION ||
    type === ExercisePrescriptionTypes.DISTANCE ||
    type === ExercisePrescriptionTypes.INTERVAL
  );
};

export const ExercisePrescriptionTypesEnum = ExercisePrescriptionTypes;



